import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Receipt, Plus, Car } from 'lucide-react';
import { api } from '../lib/api';
import { formatBRL, formatDateBR, todayISO, EXPENSE_CATEGORIES, FUEL_TYPES } from '../lib/format';
import PageHeader from '../components/PageHeader';
import EmptyState from '../components/EmptyState';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import CurrencyInput from '../components/CurrencyInput';

export default function Expenses() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterVehicle, setFilterVehicle] = useState('ALL');
  const [show, setShow] = useState(false);
  const [selectedVehicleData, setSelectedVehicleData] = useState(null);
  const [form, setForm] = useState({
    vehicle_id: '',
    category: 'PREPARATION',
    description: '',
    amount: '',
    date: todayISO(),
    paid_by_type: 'COMPANY',
    paid_by_partner_id: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterVehicle !== 'ALL') params.vehicle_id = filterVehicle;
      const [e, v, p] = await Promise.all([
        api.get('/expenses', { params }),
        api.get('/vehicles'),
        api.get('/partners'),
      ]);
      setItems(e.data);
      setVehicles(v.data);
      setPartners(p.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { 
    load();
    // Load vehicle data when filter changes
    if (filterVehicle !== 'ALL') {
      loadVehicleData(filterVehicle);
    } else {
      setSelectedVehicleData(null);
    }
    /* eslint-disable-next-line */ 
  }, [filterVehicle]);

  const vehicleLabel = (id) => {
    const v = vehicles.find((x) => x.id === id);
    return v ? `${v.brand} ${v.model} ${v.year}${v.plate ? ` - ${v.plate}` : ''}` : '—';
  };
  const partnerLabel = (id) => partners.find((p) => p.id === id)?.name || '—';

  const loadVehicleData = async (vehicleId) => {
    if (!vehicleId) {
      setSelectedVehicleData(null);
      return;
    }
    try {
      const { data } = await api.get(`/vehicles/${vehicleId}/summary`);
      setSelectedVehicleData(data.vehicle);
    } catch (err) {
      console.error('Error loading vehicle data:', err);
      setSelectedVehicleData(null);
    }
  };

  const totalSum = items.reduce((s, e) => s + (e.amount || 0), 0);

  const openCreate = () => {
    setForm({
      vehicle_id: filterVehicle !== 'ALL' ? filterVehicle : '',
      category: 'PREPARATION',
      description: '',
      amount: '',
      date: todayISO(),
      paid_by_type: 'COMPANY',
      paid_by_partner_id: '',
    });
    setShow(true);
  };

  const submit = async () => {
    if (!form.vehicle_id || !form.description || !form.amount || Number(form.amount) <= 0) {
      toast.error('Veículo, descrição e valor são obrigatórios');
      return;
    }
    if (form.paid_by_type === 'PARTNER' && !form.paid_by_partner_id) {
      toast.error('Selecione o sócio pagador');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/expenses', {
        ...form,
        amount: Number(form.amount),
        paid_by_partner_id: form.paid_by_type === 'PARTNER' ? form.paid_by_partner_id : null,
      });
      toast.success('Despesa adicionada');
      setShow(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Despesas"
        subtitle="Visão consolidada de todas as despesas registradas nos veículos da operação."
        actions={
          <Button onClick={openCreate} disabled={vehicles.length === 0} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90" data-testid="expenses-new-button">
            <Plus className="h-4 w-4 mr-1.5" /> Nova despesa
          </Button>
        }
      >
        <div className="flex gap-3 items-center">
          <div className="text-xs text-muted-foreground">Filtrar por veículo:</div>
          <Select value={filterVehicle} onValueChange={setFilterVehicle}>
            <SelectTrigger className="w-72" data-testid="expenses-vehicle-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Todos os veículos</SelectItem>
              {vehicles.map((v) => (
                <SelectItem key={v.id} value={v.id}>
                  {v.brand} {v.model} - {v.year}{v.plate ? ` - ${v.plate}` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </PageHeader>

      {selectedVehicleData && (
        <Card className="border-border/70 mb-4">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Car className="h-4 w-4 text-muted-foreground" /> Dados do veículo
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-xs text-muted-foreground">Marca</div>
              <div className="font-medium">{selectedVehicleData.brand || '—'}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Modelo</div>
              <div className="font-medium">{selectedVehicleData.model || '—'}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Versão</div>
              <div className="font-medium">{selectedVehicleData.version || '—'}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Ano modelo</div>
              <div className="font-medium">{selectedVehicleData.year || '—'}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Ano fabricação</div>
              <div className="font-medium">{selectedVehicleData.year_fabrication || '—'}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Combustível</div>
              <div className="font-medium">{FUEL_TYPES[selectedVehicleData.fuel_type] || '—'}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Cor</div>
              <div className="font-medium">{selectedVehicleData.color || '—'}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">KM</div>
              <div className="font-medium">{selectedVehicleData.km ? `${Number(selectedVehicleData.km).toLocaleString('pt-BR')} km` : '—'}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Placa</div>
              <div className="font-medium">{selectedVehicleData.plate || '—'}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Chassi</div>
              <div className="font-medium text-xs">{selectedVehicleData.chassis || '—'}</div>
            </div>
            <div className="md:col-span-2">
              <div className="text-xs text-muted-foreground">Renavam</div>
              <div className="font-medium">{selectedVehicleData.renavam || '—'}</div>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-14 rounded-xl" />)}
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={Receipt}
          title="Nenhuma despesa registrada"
          description="Adicione despesas para acompanhar custos por categoria e calcular o resultado líquido."
          action={vehicles.length > 0 ? { label: 'Adicionar despesa', onClick: openCreate } : null}
        />
      ) : (
        <Card className="border-border/70 overflow-hidden">
          <CardContent className="p-0">
            {/* Desktop table */}
            <div className="overflow-x-auto hidden md:block">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-muted-foreground">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium">Data</th>
                    <th className="text-left px-4 py-3 font-medium">Veículo</th>
                    <th className="text-left px-4 py-3 font-medium">Categoria</th>
                    <th className="text-left px-4 py-3 font-medium">Descrição</th>
                    <th className="text-left px-4 py-3 font-medium">Pago por</th>
                    <th className="text-right px-4 py-3 font-medium">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((e) => (
                    <tr key={e.id} className="border-t border-border/70 hover:bg-muted/30 cursor-pointer" onClick={() => navigate(`/veiculos/${e.vehicle_id}`)} data-testid="global-expense-row">
                      <td className="px-4 py-3 text-muted-foreground">{formatDateBR(e.date)}</td>
                      <td className="px-4 py-3">{vehicleLabel(e.vehicle_id)}</td>
                      <td className="px-4 py-3">{EXPENSE_CATEGORIES[e.category] || e.category}</td>
                      <td className="px-4 py-3">{e.description}</td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {e.paid_by_type === 'COMPANY' ? 'Empresa' : partnerLabel(e.paid_by_partner_id)}
                      </td>
                      <td className="px-4 py-3 text-right tabular font-medium">{formatBRL(e.amount)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-muted/40 border-t-2 border-border">
                    <td colSpan={5} className="px-4 py-3 text-right font-medium">Total</td>
                    <td className="px-4 py-3 text-right font-semibold tabular">{formatBRL(totalSum)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-border/70">
              {items.map((e) => (
                <button
                  key={e.id}
                  type="button"
                  onClick={() => navigate(`/veiculos/${e.vehicle_id}`)}
                  className="w-full text-left p-3 hover:bg-muted/40 transition-colors"
                  data-testid="global-expense-row"
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="text-xs text-muted-foreground flex items-center gap-1.5 flex-wrap">
                      <span className="tabular">{formatDateBR(e.date)}</span>
                      <span>·</span>
                      <span className="text-[10px] uppercase tracking-wide bg-muted px-1.5 py-0.5 rounded">{EXPENSE_CATEGORIES[e.category] || e.category}</span>
                    </div>
                    <div className="text-sm tabular font-semibold shrink-0">{formatBRL(e.amount)}</div>
                  </div>
                  <div className="text-sm">{e.description}</div>
                  <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5 flex-wrap">
                    <span>🚗 {vehicleLabel(e.vehicle_id)}</span>
                    <span>·</span>
                    <span>Pago por {e.paid_by_type === 'COMPANY' ? 'Empresa' : partnerLabel(e.paid_by_partner_id)}</span>
                  </div>
                </button>
              ))}
              <div className="bg-muted/40 p-3 flex items-center justify-between border-t-2 border-border">
                <span className="text-sm font-medium">Total</span>
                <span className="text-sm font-semibold tabular">{formatBRL(totalSum)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={show} onOpenChange={setShow}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nova despesa</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Veículo *</Label>
              <Select value={form.vehicle_id} onValueChange={(v) => setForm((f) => ({ ...f, vehicle_id: v }))}>
                <SelectTrigger data-testid="global-expense-vehicle"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                <SelectContent>
                  {vehicles.map((v) => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.brand} {v.model} - {v.year}{v.plate ? ` - ${v.plate}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Categoria</Label>
                <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(EXPENSE_CATEGORIES).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Data</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label>Descrição</Label>
              <Input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} data-testid="global-expense-description" />
            </div>
            <div>
              <Label>Valor</Label>
              <CurrencyInput value={form.amount} onChange={(v) => setForm((f) => ({ ...f, amount: v }))} data-testid="global-expense-amount" />
            </div>
            <div>
              <Label>Pago por</Label>
              <Select value={form.paid_by_type} onValueChange={(v) => setForm((f) => ({ ...f, paid_by_type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="COMPANY">Empresa (caixa comum)</SelectItem>
                  <SelectItem value="PARTNER">Sócio específico</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.paid_by_type === 'PARTNER' && (
              <div>
                <Label>Sócio</Label>
                <Select value={form.paid_by_partner_id} onValueChange={(v) => setForm((f) => ({ ...f, paid_by_partner_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                  <SelectContent>
                    {partners.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShow(false)}>Cancelar</Button>
            <Button onClick={submit} disabled={submitting} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90" data-testid="global-expense-save">
              {submitting ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
